import * as tus from 'tus-js-client';

import { FCP_OSS_ENDPOINT } from 'src/config';

import { getToken } from './auth';

export interface TusUploadOptions {
  file: File;
  dir?: string;
  onProgress?: (bytesUploaded: number, bytesTotal: number) => void;
  autoResume?: boolean;
}

export const tusUploadFile = (options: TusUploadOptions): Promise<{ name: string; url: string }> => {
  const { autoResume = true, ...rest } = options;

  const doUpload = (resume: boolean) => {
    const { file, dir, onProgress } = rest;
    return new Promise<{ name: string; url: string }>((resolve, reject) => {
      const token = getToken();
      if (!token) {
        reject(new Error('Authentication token not found'));
        return;
      }

      const metadata: Record<string, string> = {
        filename: file.name,
        filetype: file.type,
      };

      if (dir) {
        metadata.dir = dir;
      }

      const endpoint = `${FCP_OSS_ENDPOINT.replace(/\/$/, '')}/oss`;

      const uploadOptions: tus.UploadOptions = {
        endpoint,
        retryDelays: [0, 1000, 3000, 5000],
        metadata,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        onError: (error: Error) => {
          reject(error);
        },
        onProgress: (bytesUploaded: number, bytesTotal: number) => {
          onProgress?.(bytesUploaded, bytesTotal);
        },
        onSuccess: (payload?: any) => {
          try {
            let responseData: { name: string; url: string };

            if (payload?.lastResponse) {
              const fileUrl = payload.lastResponse.getHeader('X-File-URL');
              if (fileUrl) {
                responseData = { name: file.name, url: fileUrl };
              } else {
                const responseBody = payload.lastResponse.getBody();
                if (!responseBody) {
                  throw new Error('empty response body');
                }
                responseData = JSON.parse(responseBody);
              }
            } else {
              throw new Error('empty response');
            }

            resolve(responseData);
          } catch (parseError) {
            reject(new Error('Failed to parse upload response'));
          }
        },
      };

      const upload = new tus.Upload(file, uploadOptions);

      if (resume) {
        upload
          .findPreviousUploads()
          .then((previousUploads) => {
            if (previousUploads.length) {
              upload.resumeFromPreviousUpload(previousUploads[0]);
            }
            upload.start();
          })
          .catch(() => {
            upload.start();
          });
      } else {
        upload.start();
      }
    });
  };

  if (autoResume) {
    return doUpload(true).catch(() => doUpload(false));
  } else {
    return doUpload(false);
  }
};
